import { useTranslations } from '@vocab/react';
import { Text, Box } from 'braid-design-system';
import clsx from 'clsx';
import { omit, debounce } from 'lodash';
import { useState, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';

import { usePrevious } from 'src/modules/hooks/usePrevious';
import type {
  DoneLabel,
  SyncingLabel,
  SyncingState,
} from 'src/modules/syncing';

import translations from './.vocab';
import { Spinner } from './Spinner/Spinner';
import { Tick } from './Tick/Tick';

import * as styles from './Sync.css';

interface Props {
  className?: string;
  syncingLabel?: SyncingLabel;
  doneLabel?: DoneLabel;
  whiteMode?: boolean;
  state: SyncingState;
  doneTimeout?: number;
  onDone?: () => void;
}

const renderText = ({
  icon,
  label,
  labelTone,
  whiteMode,
}: {
  icon: React.ReactElement;
  label: string;
  labelTone: 'secondary' | 'positive';
  whiteMode: boolean;
}) => (
  <Box display="flex" alignItems="center">
    {icon}
    <Text tone={whiteMode ? undefined : labelTone} size="small">
      &nbsp;&nbsp;{label}
    </Text>
  </Box>
);

export const Sync = ({
  state,
  className = '',
  syncingLabel = 'Saving',
  doneLabel = 'Saved',
  whiteMode = false,
  doneTimeout = 4000,
  onDone,
  ...restProps
}: Props) => {
  const { t } = useTranslations(translations);

  const translatedSyncingLabel = t(syncingLabel);
  const translatedDoneLabel = t(doneLabel);

  const [fadeOut, setFadeOut] = useState(false);

  const onFadeOut = useRef(
    debounce(() => {
      setFadeOut(false);
      onDone?.();
    }, doneTimeout),
  );

  const prevState = usePrevious(state);

  useEffect(() => {
    if (prevState === 'SYNCING') {
      if (state === 'SYNCED') {
        setFadeOut(true);
        onFadeOut.current();
      } else if (state === 'ERROR') {
        if (onDone) {
          onDone();
        }
      }
    }
  }, [onDone, onFadeOut, state, prevState]);

  useEffect(() => {
    onFadeOut.current?.cancel();
  }, []);

  const rootStyles = clsx({
    [styles.root]: true,
    [className ? className : '']: className,
  });

  const spinnerClassName = clsx({
    [styles.spinnerSvg]: true,
    [styles.spinnerSvgWhiteMode]: whiteMode,
  });

  const tickClassName = clsx({
    [styles.tickSvg]: true,
    [styles.tickSvgWhiteMode]: whiteMode,
  });

  const props = omit(restProps, 'doneTimeout', 'onDone');

  return (
    <div className={rootStyles} {...props}>
      <CSSTransition
        in={state === 'SYNCING'}
        timeout={{ exit: 400, enter: 500 }}
        unmountOnExit
        classNames={{
          enter: styles.enter,
          enterActive: styles.enterActiveSyncing,
          exit: styles.exit,
          exitActive: styles.exitActive,
        }}
      >
        <span className={styles.container} aria-live="polite">
          {renderText({
            icon: <Spinner svgClassName={spinnerClassName} />,
            label: translatedSyncingLabel,
            labelTone: 'secondary',
            whiteMode,
          })}
        </span>
      </CSSTransition>
      <CSSTransition
        in={state === 'SYNCED' && fadeOut}
        unmountOnExit
        timeout={{ exit: 500, enter: 1000 }}
        classNames={{
          enter: styles.enter,
          enterActive: styles.enterActiveDone,
          exit: styles.exit,
          exitActive: styles.exitActive,
        }}
      >
        <span className={styles.container} aria-live="polite">
          {renderText({
            icon: <Tick svgClassName={tickClassName} />,
            label: translatedDoneLabel,
            labelTone: 'positive',
            whiteMode,
          })}
        </span>
      </CSSTransition>
      <span className={styles.hiddenContainer}>
        {renderText({
          icon: <Spinner svgClassName={spinnerClassName} />,
          label: translatedSyncingLabel,
          labelTone: 'secondary',
          whiteMode,
        })}
      </span>
      <span className={styles.hiddenContainer}>
        {renderText({
          icon: <Tick svgClassName={tickClassName} />,
          label: translatedDoneLabel,
          labelTone: 'positive',
          whiteMode,
        })}
      </span>
    </div>
  );
};
